import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { TextInput } from '@/components/form/TextInput.tsx';
import useSetBookingCodePrefix from '@/features/dashboard/bookingCodePrefix/hooks/useSetBookingCodePrefix.ts';
import {
  bookingCodePrefixSchema,
  BookingCodePrefixFormData,
} from '@/features/dashboard/bookingCodePrefix/schemas/bookingCodePrefixSchema.ts';

interface EditBookingCodePrefixModalProps {
  currentPrefix: string | null;
  onClose: () => void;
}

const EditBookingCodePrefixModal = ({
  currentPrefix,
  onClose,
}: EditBookingCodePrefixModalProps) => {
  const setMutation = useSetBookingCodePrefix();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<BookingCodePrefixFormData>({
    resolver: zodResolver(bookingCodePrefixSchema),
    defaultValues: { prefix: currentPrefix ?? '' },
  });

  const onSubmit = (data: BookingCodePrefixFormData) => {
    const trimmed = data.prefix.trim();
    setMutation.mutate(
      { prefix: trimmed === '' ? null : trimmed },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Modal onClose={onClose} className="max-w-[480px]">
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader
          title="Edytuj prefiks numeru rezerwacji"
          onClose={onClose}
        />
        <ModalContent>
          <TextInput
            label="Prefiks"
            register={register('prefix')}
            placeholder="np. CAMP"
            error={errors.prefix?.message}
            hint="Maksymalnie 6 znaków, wielkie litery i cyfry. Zostaw puste, by usunąć prefiks."
            onChange={(event) => {
              setValue('prefix', event.target.value.toUpperCase(), {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
          />
        </ModalContent>
        <ModalFooter>
          <ActionButton tone="ghost" size="sm" type="button" onClick={onClose}>
            Anuluj
          </ActionButton>
          <ActionButton
            tone="accent"
            size="sm"
            type="submit"
            isLoading={setMutation.isPending}
          >
            Zapisz
          </ActionButton>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default EditBookingCodePrefixModal;
